import React, { createContext, ReactNode, useMemo } from 'react';
import { useGetMemberBffData } from '../../api/member';
import useContext from '../../hooks/useContext';

interface IMemberContext {
  authorized: boolean;
  memberNumber?: string;
  member?: Member.IBffMember;
}

const AUTH_COOKIE_NAME = 'authorized';
const MEMBER_COOKIE_NAME = 'member_number';

const MemberContext = createContext<undefined | IMemberContext>(undefined);

const getCookieValue = (name: string) =>
  document.cookie
    ?.split('; ')
    ?.find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];

const getAuthTimestamp = () => {
  if (SSR) {
    return undefined;
  }

  try {
    const cookieValue = getCookieValue(AUTH_COOKIE_NAME);

    return cookieValue ? new Date(decodeURIComponent(cookieValue)).getTime() : undefined;
  } catch {
    return undefined;
  }
};

export const getMemberNumber = () => {
  if (SSR) {
    return undefined;
  }

  try {
    return getCookieValue(MEMBER_COOKIE_NAME);
  } catch {
    return undefined;
  }
};

interface IMemberProvider {
  children: ReactNode;
}

const MemberProvider = ({ children }: IMemberProvider) => {
  const authorized = !!getAuthTimestamp();
  const memberNumber = getMemberNumber();
  const { data: member } = useGetMemberBffData(!memberNumber);

  const value = useMemo(() => ({ authorized, memberNumber, member }), [authorized, member]);

  return <MemberContext.Provider value={value}>{children}</MemberContext.Provider>;
};

export default MemberProvider;

export const useMember = () => {
  return useContext(MemberContext);
};

export const useIsAuthorized = () => useMember()?.authorized;

export const useMemberNumber = () => useMember()?.memberNumber;
